import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {

  Alert,

  Badge,

  Button,

  ButtonGroup,

  Card,

  Col,

  Form,

  Nav,

  Row,

  Spinner,

  Tab,

  Table,

} from 'react-bootstrap';

import axios from 'axios';

import { Navigate } from 'react-router-dom';

import LegacyAttachmentList, { isPreviewableLegacyAttachment } from '../components/LegacyAttachmentList';

import { API_URL } from '../config';

import { useAuth } from '../context/AuthContext';
import { getAuthToken } from '../utils/authToken';

import type {
  ServiceNowLegacyAskResponse,
  ServiceNowLegacyAttachmentsResponse,
  ServiceNowLegacyAttachmentItem,
  ServiceNowLegacySqlResponse,
  ServiceNowLegacyStatusResponse,
} from '../types';

import './ServiceNowLegacyExplorer.css';



type ChatMessage = {

  id: string;

  role: 'user' | 'assistant';

  content: string;

  meta?: string;

  attachments?: ServiceNowLegacyAttachmentItem[];

  queryResult?: ServiceNowLegacySqlResponse;

};



const canAccessLegacyExplorer = (role?: string | null): boolean =>

  role === 'admin' || role === 'analista';



const SYS_ID_RE = /^[0-9a-f]{32}$/i;
const RECORD_NUMBER_RE = /\b(INC|PRB|CHG)\d+\b/gi;

const extractRecordNumbers = (text: string): string[] => {
  const matches = text.match(RECORD_NUMBER_RE) || [];
  return Array.from(new Set(matches.map((m) => m.toUpperCase())));
};



const SQL_INCIDENT_WITH_ATTACHMENTS = `SELECT
  i.number,
  i.short_description,
  i.state,
  i.priority,
  i.opened_at,
  i.closed_at,
  i.description,
  a.sys_id AS attachment_sys_id,
  a.file_name,
  a.content_type,
  a.size_bytes
FROM ar_incident i
LEFT JOIN sys_attachment a ON a.table_sys_id = i.sys_id
WHERE i.number = 'INC26007017'`;

const SQL_ATTACHMENTS_ONLY = `SELECT a.sys_id, a.file_name, a.content_type, a.size_bytes, i.number
FROM sys_attachment a
JOIN ar_incident i ON i.sys_id = a.table_sys_id
WHERE i.number = 'INC26007017'`;

const SQL_COLUMN_LABELS: Record<string, string> = {
  number: 'Número',
  short_description: 'Resumo',
  description: 'Descrição',
  state: 'Estado',
  priority: 'Prioridade',
  urgency: 'Urgência',
  impact: 'Impacto',
  category: 'Categoria',
  subcategory: 'Subcategoria',
  u_categorization: 'Categorização',
  assignment_group: 'Grupo de atribuição',
  u_owner_group: 'Grupo responsável',
  grupo_atribuicao: 'Grupo de atribuição',
  grupo_responsavel: 'Grupo responsável',
  categorizacao_si: 'Categorização (SI)',
  total_chamados: 'Total de chamados',
  assigned_to: 'Atribuído a',
  caller_id: 'Solicitante',
  opened_at: 'Aberto em',
  resolved_at: 'Resolvido em',
  closed_at: 'Fechado em',
  close_code: 'Código de fechamento',
  close_notes: 'Notas de encerramento',
  work_notes: 'Notas de trabalho',
  comments: 'Comentários',
  problem_id: 'Problem (sys_id)',
  parent_incident: 'Incidente pai',
  sys_id: 'Sys ID',
  sys_created_on: 'Criado em',
  sys_updated_on: 'Atualizado em',
  file_name: 'Arquivo',
  content_type: 'Tipo',
  size_bytes: 'Tamanho (bytes)',
  attachment_sys_id: 'Anexo (sys_id)',
  element: 'Campo',
  value: 'Conteúdo',
  sys_created_by: 'Autor',
};

const SQL_FIELD_ORDER = [
  'number', 'short_description', 'description', 'state', 'priority', 'urgency', 'impact',
  'category', 'subcategory', 'u_categorization', 'assignment_group', 'u_owner_group', 'assigned_to', 'caller_id',
  'opened_at', 'resolved_at', 'closed_at', 'close_code', 'close_notes', 'work_notes', 'comments',
  'problem_id', 'parent_incident', 'sys_id', 'sys_created_on', 'sys_updated_on',
  'attachment_sys_id', 'file_name', 'content_type', 'size_bytes',
];

const isLongTextSqlColumn = (col: string, value?: unknown): boolean => {
  const key = col.toLowerCase();
  if (['value', 'body', 'message', 'text', 'payload', 'content'].includes(key)) return true;
  if (key.includes('description') || key.includes('note') || key.includes('comment') || key.includes('work_')) {
    return true;
  }
  if (value != null) {
    const text = String(value);
    if (text.length > 100 || text.includes('\n')) return true;
  }
  return false;
};

const findSqlColumn = (columns: string[], ...names: string[]): string | undefined => {
  const map = Object.fromEntries(columns.map((c) => [c.toLowerCase(), c]));
  for (const name of names) {
    const hit = map[name];
    if (hit) return hit;
  }
  return undefined;
};

const isJournalLikeResult = (columns: string[]): boolean => {
  const lower = columns.map((c) => c.toLowerCase());
  return lower.includes('value') && (lower.includes('element') || lower.includes('element_id'));
};

const shouldPreferSqlDetailView = (rowCount: number, colCount: number): boolean => {
  if (colCount <= 3) return false;
  return rowCount > 0 && rowCount <= 8 && colCount >= 6;
};

const resolveSqlResultLayout = (result: ServiceNowLegacySqlResponse): 'journal' | 'detail' | 'table' => {
  if (result.row_count === 0) return 'table';
  if (isJournalLikeResult(result.columns)) return 'journal';
  if (shouldPreferSqlDetailView(result.row_count, result.columns.length)) return 'detail';
  if (result.row_count <= 3 && result.columns.length >= 4) return 'detail';
  return 'table';
};

const getSqlCellClass = (col: string, value: unknown): string =>
  isLongTextSqlColumn(col, value) ? 'sn-legacy-cell-long' : 'sn-legacy-cell-short';

const humanizeSqlColumn = (col: string): string => {
  const key = col.toLowerCase();
  return SQL_COLUMN_LABELS[key] ?? col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const sortSqlColumns = (columns: string[]): string[] => {
  const order = new Map(SQL_FIELD_ORDER.map((name, index) => [name, index]));
  return [...columns].sort((a, b) => {
    const left = order.get(a.toLowerCase()) ?? 999;
    const right = order.get(b.toLowerCase()) ?? 999;
    if (left !== right) return left - right;
    return a.localeCompare(b);
  });
};

const formatSqlCellValue = (value: unknown): string => {
  if (value == null || value === '') return '—';
  return String(value);
};

const renderMarkdownLite = (text: string): React.ReactNode => {
  const lines = text.split('\n');

  const renderInline = (line: string): string => {
    const bold = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    return bold.replace(/_(.+?)_/g, '<em>$1</em>');
  };

  return lines.map((line, index) => {
    if (line.startsWith('| ') && line.includes('|')) {
      return (
        <div key={`${index}-table-line`} className="sn-legacy-table-line">
          {line}
        </div>
      );
    }

    if (line.startsWith('## ')) {
      return (
        <h4
          key={`${index}-h2`}
          className="sn-legacy-heading"
          dangerouslySetInnerHTML={{ __html: renderInline(line.slice(3)) }}
        />
      );
    }

    if (line.startsWith('### ')) {
      return (
        <h5
          key={`${index}-h3`}
          className="sn-legacy-subheading"
          dangerouslySetInnerHTML={{ __html: renderInline(line.slice(4)) }}
        />
      );
    }

    if (line.startsWith('- ')) {
      return (
        <div
          key={`${index}-li`}
          className="sn-legacy-list-item"
          dangerouslySetInnerHTML={{ __html: renderInline(line.slice(2)) }}
        />
      );
    }

    if (line.trim() === '') {
      return <div key={`${index}-sp`} className="sn-legacy-spacer" aria-hidden />;
    }

    return (
      <p
        key={`${index}-p`}
        className="sn-legacy-paragraph"
        dangerouslySetInnerHTML={{ __html: renderInline(line) }}
      />
    );
  });
};



const findSqlAttachmentItem = (

  row: Record<string, unknown>,

  columns: string[],

  attachments: ServiceNowLegacyAttachmentItem[] | undefined,

): ServiceNowLegacyAttachmentItem | null => {

  const colMap = Object.fromEntries(columns.map((c) => [c.toLowerCase(), c]));

  const sysIdCol = colMap.sys_id;

  const fileNameCol = colMap.file_name;

  if (!sysIdCol || !fileNameCol) return null;

  const sysId = String(row[sysIdCol] ?? '').trim().toLowerCase();

  const fileName = row[fileNameCol];

  if (!SYS_ID_RE.test(sysId) || !fileName) return null;

  return attachments?.find((a) => a.sys_id === sysId) ?? {

    sys_id: sysId,

    file_name: String(fileName),

    content_type: String(row[colMap.content_type ?? ''] ?? 'application/octet-stream'),

    size_bytes: row[colMap.size_bytes ?? ''] as number | null | undefined,

  };

};



const SqlRowAttachmentActions: React.FC<{

  item: ServiceNowLegacyAttachmentItem;

  authHeader: Record<string, string>;

  onError: (message: string) => void;

}> = ({ item, authHeader, onError }) => {

  const [downloading, setDownloading] = useState(false);



  const download = async (inline: boolean) => {

    if (downloading) return;

    setDownloading(true);

    try {

      const response = await axios.get(

        `${API_URL}/api/servicenow-legacy/attachments/${item.sys_id}/download`,

        { headers: authHeader, params: { inline }, responseType: 'blob' },

      );

      const blob = new Blob([response.data], {

        type: response.headers['content-type'] || item.content_type || 'application/octet-stream',

      });

      const url = window.URL.createObjectURL(blob);

      if (inline) {

        window.open(url, '_blank', 'noopener,noreferrer');

        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);

      } else {

        const link = document.createElement('a');

        link.href = url;

        link.download = item.file_name || 'anexo';

        document.body.appendChild(link);

        link.click();

        link.remove();

        window.URL.revokeObjectURL(url);

      }

    } catch (err: unknown) {

      const detail = axios.isAxiosError(err)

        ? (err.response?.data as { detail?: string })?.detail

        : null;

      onError(detail || `Falha ao baixar ${item.file_name}.`);

    } finally {

      setDownloading(false);

    }

  };



  return (

    <>

      <Button size="sm" variant="outline-primary" disabled={downloading} onClick={() => void download(false)}>

        Baixar

      </Button>

      {isPreviewableLegacyAttachment(item) && (

        <Button

          size="sm"

          variant="outline-secondary"

          className="ms-1"

          disabled={downloading}

          onClick={() => void download(true)}

        >

          Abrir

        </Button>

      )}

    </>

  );

};

type SqlResultViewMode = 'auto' | 'detail' | 'table';

type SqlResultDataProps = {
  result: ServiceNowLegacySqlResponse;
  viewMode: SqlResultViewMode;
  sqlHasInlineAttachments: boolean;
  authHeader: Record<string, string>;
  onAttachmentError: (message: string) => void;
  findSqlAttachmentItem: (
    row: Record<string, unknown>,
    columns: string[],
    attachments: ServiceNowLegacyAttachmentItem[] | undefined,
  ) => ServiceNowLegacyAttachmentItem | null;
};

const SqlResultDetailView: React.FC<{
  result: ServiceNowLegacySqlResponse;
}> = ({ result }) => {
  const columns = sortSqlColumns(result.columns);

  return (
    <div className="sn-legacy-detail-list">
      {result.rows.map((row, rowIndex) => {
        const title = formatSqlCellValue(row.number ?? row.Number ?? `Registro ${rowIndex + 1}`);
        return (
          <Card key={`detail-${rowIndex}`} className="sn-legacy-record-detail">
            <Card.Header className="sn-legacy-record-detail-header">{title}</Card.Header>
            <Card.Body>
              <div className="sn-legacy-field-grid">
                {columns.map((col) => {
                  const value = formatSqlCellValue(row[col]);
                  if (value === '—') return null;
                  return (
                    <div
                      key={`${rowIndex}-${col}`}
                      className={`sn-legacy-field${isLongTextSqlColumn(col, row[col]) ? ' sn-legacy-field-long' : ''}`}
                    >
                      <div className="sn-legacy-field-label">{humanizeSqlColumn(col)}</div>
                      <div className="sn-legacy-field-value">{value}</div>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

const SqlResultJournalView: React.FC<{
  result: ServiceNowLegacySqlResponse;
}> = ({ result }) => {
  const elementCol = findSqlColumn(result.columns, 'element', 'element_id');
  const valueCol = findSqlColumn(result.columns, 'value');
  const dateCol = findSqlColumn(result.columns, 'sys_created_on', 'created_on', 'sys_updated_on');
  const authorCol = findSqlColumn(result.columns, 'sys_created_by', 'created_by', 'author');

  return (
    <div className="sn-legacy-journal-list">
      {result.rows.map((row, rowIndex) => {
        const element = elementCol ? formatSqlCellValue(row[elementCol]) : null;
        const value = valueCol ? formatSqlCellValue(row[valueCol]) : null;
        const date = dateCol ? formatSqlCellValue(row[dateCol]) : null;
        const author = authorCol ? formatSqlCellValue(row[authorCol]) : null;
        const prominentCols = new Set(
          [elementCol, valueCol, dateCol, authorCol].filter(Boolean) as string[],
        );
        const extraColumns = result.columns.filter((col) => !prominentCols.has(col));

        return (
          <div key={`journal-${rowIndex}`} className="sn-legacy-journal-entry">
            <div className="sn-legacy-journal-meta">
              {element && element !== '—' ? (
                <Badge bg="secondary" className="sn-legacy-journal-badge">{element}</Badge>
              ) : null}
              {date && date !== '—' ? <span className="sn-legacy-journal-date">{date}</span> : null}
              {author && author !== '—' ? <span className="sn-legacy-journal-author">{author}</span> : null}
            </div>
            {value && value !== '—' ? (
              <div className="sn-legacy-journal-text">{value}</div>
            ) : null}
            {(value === '—' || !valueCol) && (
              <div className="sn-legacy-field-grid sn-legacy-journal-fallback-grid">
                {result.columns.map((col) => {
                  if (prominentCols.has(col)) return null;
                  const cell = formatSqlCellValue(row[col]);
                  if (cell === '—') return null;
                  return (
                    <div key={`${rowIndex}-${col}-primary`} className="sn-legacy-field">
                      <div className="sn-legacy-field-label">{humanizeSqlColumn(col)}</div>
                      <div className="sn-legacy-field-value">{cell}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {extraColumns.length > 0 && (
              <div className="sn-legacy-field-grid sn-legacy-journal-extra-grid">
                {extraColumns.map((col) => {
                  const cell = formatSqlCellValue(row[col]);
                  if (cell === '—') return null;
                  return (
                    <div key={`${rowIndex}-${col}-extra`} className="sn-legacy-field">
                      <div className="sn-legacy-field-label">{humanizeSqlColumn(col)}</div>
                      <div className="sn-legacy-field-value">{cell}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SqlResultTableView: React.FC<SqlResultDataProps> = ({
  result,
  sqlHasInlineAttachments,
  authHeader,
  onAttachmentError,
  findSqlAttachmentItem,
}) => (
  <div className="table-responsive sn-legacy-table-wrap">
    <Table
      striped
      bordered
      hover
      size="sm"
      className={`sn-legacy-result-table mb-0${result.columns.length <= 4 ? ' sn-legacy-result-table-fluid' : ''}`}
    >
      <thead>
        <tr>
          {result.columns.map((col) => (
            <th key={col} title={col}>{humanizeSqlColumn(col)}</th>
          ))}
          {sqlHasInlineAttachments && <th>Ações</th>}
        </tr>
      </thead>
      <tbody>
        {result.rows.length === 0 && (
          <tr>
            <td
              colSpan={Math.max(result.columns.length, 1) + (sqlHasInlineAttachments ? 1 : 0)}
              className="text-center text-muted"
            >
              Nenhuma linha retornada.
            </td>
          </tr>
        )}
        {result.rows.map((row, rowIndex) => {
          const item = findSqlAttachmentItem(row, result.columns, result.attachments);
          return (
            <tr key={`row-${rowIndex}`}>
              {result.columns.map((col) => (
                <td
                  key={`${rowIndex}-${col}`}
                  className={getSqlCellClass(col, row[col])}
                  title={formatSqlCellValue(row[col])}
                >
                  {formatSqlCellValue(row[col])}
                </td>
              ))}
              {sqlHasInlineAttachments && (
                <td className="sn-legacy-attachment-actions">
                  {item ? (
                    <SqlRowAttachmentActions
                      item={item}
                      authHeader={authHeader}
                      onError={onAttachmentError}
                    />
                  ) : '—'}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </Table>
  </div>
);

const SqlResultDataView: React.FC<SqlResultDataProps> = (props) => {
  const autoLayout = resolveSqlResultLayout(props.result);
  const effectiveLayout =
    props.viewMode === 'table'
      ? 'table'
      : props.viewMode === 'detail'
        ? (autoLayout === 'journal' ? 'journal' : 'detail')
        : autoLayout;

  if (effectiveLayout === 'journal') {
    return <SqlResultJournalView result={props.result} />;
  }
  if (effectiveLayout === 'detail') {
    return <SqlResultDetailView result={props.result} />;
  }
  return <SqlResultTableView {...props} />;
};

const ServiceNowLegacyExplorer: React.FC = () => {

  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<'natural' | 'sql'>('natural');

  const [status, setStatus] = useState<ServiceNowLegacyStatusResponse | null>(null);

  const [statusLoading, setStatusLoading] = useState(true);

  const [statusError, setStatusError] = useState<string | null>(null);



  const [nlInput, setNlInput] = useState('');

  const [nlLoading, setNlLoading] = useState(false);

  const [nlMessages, setNlMessages] = useState<ChatMessage[]>([]);

  const [nlAttachmentError, setNlAttachmentError] = useState<string | null>(null);



  const [sqlInput, setSqlInput] = useState(SQL_INCIDENT_WITH_ATTACHMENTS);

  const [sqlLoading, setSqlLoading] = useState(false);

  const [sqlResult, setSqlResult] = useState<ServiceNowLegacySqlResponse | null>(null);
  const [sqlViewMode, setSqlViewMode] = useState<SqlResultViewMode>('auto');

  const [sqlError, setSqlError] = useState<string | null>(null);

  const [sqlAttachmentError, setSqlAttachmentError] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement | null>(null);



  const authHeader = useMemo(

    () => ({ Authorization: `Bearer ${getAuthToken() || ''}` }),

    [user?.id],

  );



  const loadStatus = useCallback(async () => {

    setStatusLoading(true);

    setStatusError(null);

    try {

      const { data } = await axios.get<ServiceNowLegacyStatusResponse>(

        `${API_URL}/api/servicenow-legacy/status`,

        { headers: authHeader },

      );

      setStatus(data);

    } catch (err: unknown) {

      const detail = axios.isAxiosError(err)

        ? (err.response?.data as { detail?: string })?.detail

        : null;

      setStatusError(detail || 'Não foi possível carregar o status do banco legado.');

    } finally {

      setStatusLoading(false);

    }

  }, [authHeader]);



  useEffect(() => {
    if (canAccessLegacyExplorer(user?.role)) {
      void loadStatus();
    }
  }, [loadStatus, user?.role]);



  useEffect(() => {

    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  }, [nlMessages, nlLoading]);



  if (!canAccessLegacyExplorer(user?.role)) {

    return <Navigate to="/dashboard" replace />;

  }



  const fetchAttachmentsForNumber = async (
    number: string,
  ): Promise<{ items?: ServiceNowLegacyAttachmentItem[]; notFound?: boolean }> => {
    try {
      const { data } = await axios.get<ServiceNowLegacyAttachmentsResponse>(
        `${API_URL}/api/servicenow-legacy/attachments`,
        { headers: authHeader, params: { number } },
      );
      const recordNumber = data.record.number;
      return {
        items: data.attachments.map((item: ServiceNowLegacyAttachmentItem) => ({
          ...item,
          record_number: recordNumber,
        })),
      };
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return { notFound: true };
      }
      return {};
    }
  };

  const handleNaturalAsk = async (event: React.FormEvent) => {

    event.preventDefault();

    const question = nlInput.trim();

    if (!question || nlLoading) return;



    setNlMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: question }]);

    setNlInput('');

    setNlLoading(true);

    setNlAttachmentError(null);

    const recordNumbers = extractRecordNumbers(question);
    let attachmentsFromNumber: ServiceNowLegacyAttachmentItem[] | undefined;
    let recordNotFound: string | undefined;
    if (recordNumbers.length && status?.connected) {
      const attLookup = await fetchAttachmentsForNumber(recordNumbers[0]);
      attachmentsFromNumber = attLookup.items;
      if (attLookup.notFound) recordNotFound = recordNumbers[0];
    }

    try {

      const { data } = await axios.post<ServiceNowLegacyAskResponse>(

        `${API_URL}/api/servicenow-legacy/ask`,

        { question },

        { headers: authHeader },

      );

      const mergedAttachments = data.attachments?.length
        ? data.attachments
        : attachmentsFromNumber;

      const metaParts: string[] = [];

      if (data.row_count > 0) {

        metaParts.push(`${data.row_count} linha(s) · ${data.execution_ms} ms`);

        if (data.truncated) metaParts.push('truncado');

      }

      if (mergedAttachments?.length) metaParts.push(`${mergedAttachments.length} anexo(s)`);
      if (data.answer_mode === 'structured') metaParts.push('modo banco (sem IA)');
      else if (data.answer_mode === 'openai') metaParts.push('resposta assistida');



      const queryResult: ServiceNowLegacySqlResponse | undefined =
        data.columns?.length && data.rows
          ? {
              columns: data.columns,
              rows: data.rows,
              row_count: data.row_count,
              truncated: data.truncated,
              execution_ms: data.execution_ms,
              sql_executed: data.sql_executed || '',
              attachments: mergedAttachments,
            }
          : undefined;

      setNlMessages((prev) => [

        ...prev,

        {

          id: `a-${Date.now()}`,

          role: 'assistant',

          content: data.answer,

          meta: metaParts.length ? metaParts.join(' · ') : undefined,

          attachments: mergedAttachments?.length ? mergedAttachments : undefined,

          queryResult,

        },

      ]);

    } catch (err: unknown) {

      const detail = axios.isAxiosError(err)

        ? (err.response?.data as { detail?: string })?.detail

        : null;

      const fallbackMessage = recordNotFound
        ? `O registro **${recordNotFound}** não foi encontrado no backup legado. Verifique o número ou use a aba SQL.`
        : (detail || 'Não foi possível processar a pergunta.');

      setNlMessages((prev) => [

        ...prev,

        {
          id: `e-${Date.now()}`,
          role: 'assistant',
          content: fallbackMessage,
          attachments: attachmentsFromNumber?.length ? attachmentsFromNumber : undefined,
          meta: attachmentsFromNumber?.length
            ? `${attachmentsFromNumber.length} anexo(s) · consulta IA indisponível`
            : undefined,
        },

      ]);

    } finally {

      setNlLoading(false);

    }

  };



  const handleSqlExecute = async (event: React.FormEvent) => {

    event.preventDefault();

    const sql = sqlInput.trim();

    if (!sql || sqlLoading) return;



    setSqlLoading(true);

    setSqlError(null);

    setSqlAttachmentError(null);

    setSqlResult(null);
    setSqlViewMode('auto');

    try {

      const { data } = await axios.post<ServiceNowLegacySqlResponse>(

        `${API_URL}/api/servicenow-legacy/sql`,

        { sql },

        { headers: authHeader },

      );

      setSqlResult(data);

    } catch (err: unknown) {

      const detail = axios.isAxiosError(err)

        ? (err.response?.data as { detail?: string })?.detail

        : null;

      setSqlError(detail || 'Falha ao executar SQL.');

    } finally {

      setSqlLoading(false);

    }

  };



  const sqlHasInlineAttachments = sqlResult

    ? sqlResult.rows.some((row) => findSqlAttachmentItem(row, sqlResult.columns, sqlResult.attachments))

    : false;



  return (

    <div className="sn-legacy-page">

      <div className="sn-legacy-header">

        <div>

          <h2 className="mb-1">Consulta ServiceNow (legado)</h2>

          <p className="text-muted mb-0">

            Explore o backup legado do ServiceNow com linguagem natural ou SQL read-only.

          </p>

        </div>

        <Button variant="outline-secondary" size="sm" onClick={() => void loadStatus()} disabled={statusLoading}>

          Atualizar status

        </Button>

      </div>



      {statusLoading && (

        <div className="sn-legacy-status-loading">

          <Spinner animation="border" size="sm" /> Verificando conexão...

        </div>

      )}



      {!statusLoading && statusError && <Alert variant="danger">{statusError}</Alert>}

      {!statusLoading && status && !status.connected && (
        <Alert variant="warning" className="mb-3">
          {!status.configured ? (
            <>
              O banco legado ServiceNow não está configurado no backend. Defina as variáveis{' '}
              <code>SERVICENOW_DB_*</code> no <code>.env.development</code> e recrie o container backend.
            </>
          ) : status.password_configured === false ? (
            <>
              A senha do RDS legado não está configurada nesta máquina. Preencha{' '}
              <code>SERVICENOW_DB_PASSWORD</code> no <code>.env.development</code> ou crie o arquivo{' '}
              <code>backend/servicenow_db_password.txt</code> (uma linha com a senha) e execute{' '}
              <code>docker compose restart backend</code>. A senha está no Elastic Beanstalk (dev) ou com o admin da infra.
            </>
          ) : (
            <>
              Não foi possível conectar ao RDS legado. Verifique VPN/rede, credenciais e se o host{' '}
              <code>SERVICENOW_DB_HOST</code> está acessível a partir desta máquina.
            </>
          )}
        </Alert>
      )}

      {!statusLoading && status && (

        <Card className="sn-legacy-status-card mb-4">

          <Card.Body className="d-flex flex-wrap gap-2 align-items-center">

            <Badge bg={status.connected ? 'success' : 'secondary'}>

              {status.connected
                ? 'Backup conectado'
                : status.password_configured === false
                  ? 'Senha não configurada'
                  : !status.configured
                    ? 'Backup não configurado'
                    : 'Backup indisponível'}

            </Badge>

            <Badge
              bg={
                !status.openai_configured
                  ? 'warning'
                  : status.openai_quota_exhausted
                    ? 'danger'
                    : 'info'
              }
            >
              {!status.openai_configured
                ? 'Assistente indisponível'
                : status.openai_quota_exhausted
                  ? 'Assistente temporariamente indisponível'
                  : 'Assistente disponível'}
            </Badge>

            {status.table_count != null && (

              <Badge bg="light" text="dark">{status.table_count} tabelas</Badge>

            )}

            <Badge bg="light" text="dark">
              {status.max_rows > 0 ? `SQL até ${status.max_rows} linhas` : 'SQL sem limite de linhas'}
            </Badge>

          </Card.Body>

        </Card>

      )}



      <Tab.Container

        activeKey={activeTab}

        onSelect={(k) => setActiveTab((k as 'natural' | 'sql') || 'natural')}

      >

        <Nav variant="tabs" className="sn-legacy-tabs mb-3">

          <Nav.Item><Nav.Link eventKey="natural">Linguagem natural</Nav.Link></Nav.Item>

          <Nav.Item><Nav.Link eventKey="sql">SQL direto (raw)</Nav.Link></Nav.Item>

        </Nav>



        <Tab.Content>

          <Tab.Pane eventKey="natural">

            <Row className="g-3">

              <Col lg={8}>

                <Card className="sn-legacy-chat-card">

                  <Card.Body className="sn-legacy-chat-body">

                    {nlMessages.length === 0 && (

                      <div className="sn-legacy-empty">

                        <p className="mb-2"><strong>Exemplos de perguntas</strong></p>

                        <ul>

                          <li>Me fale sobre o INC26007017 e seus anexos.</li>

                          <li>Quais anexos existem no incidente INC55846295?</li>

                          <li>Liste os 10 incidentes abertos mais recentes.</li>

                        </ul>

                      </div>

                    )}

                    {nlMessages.map((msg) => (

                      <div key={msg.id} className={`sn-legacy-bubble sn-legacy-bubble-${msg.role}`}>

                        <div className="sn-legacy-bubble-content">

                          {msg.role === 'assistant' ? renderMarkdownLite(msg.content) : msg.content}

                        </div>

                        {msg.meta && <small className="sn-legacy-meta">{msg.meta}</small>}

                        {msg.attachments?.length ? (

                          <LegacyAttachmentList

                            items={msg.attachments}

                            authHeader={authHeader}

                            compact

                            title="Anexos relacionados"

                            onError={setNlAttachmentError}

                          />

                        ) : null}

                        {msg.queryResult && msg.queryResult.row_count > 0 ? (
                          <div className="sn-legacy-chat-query-data mt-2">
                            {msg.queryResult.sql_executed ? (
                              <pre className="sn-legacy-sql-executed mb-2">{msg.queryResult.sql_executed}</pre>
                            ) : null}
                            {msg.queryResult.truncated ? (
                              <Alert variant="warning" className="py-2 mb-2">
                                Resultado truncado — refine a pergunta ou use SQL direto para ver mais linhas.
                              </Alert>
                            ) : null}
                            <SqlResultDataView
                              result={msg.queryResult}
                              viewMode="auto"
                              sqlHasInlineAttachments={msg.queryResult.rows.some((row) =>
                                findSqlAttachmentItem(row, msg.queryResult!.columns, msg.queryResult!.attachments),
                              )}
                              authHeader={authHeader}
                              onAttachmentError={setNlAttachmentError}
                              findSqlAttachmentItem={findSqlAttachmentItem}
                            />
                          </div>
                        ) : null}

                      </div>

                    ))}

                    {nlLoading && (

                      <div className="sn-legacy-bubble sn-legacy-bubble-assistant">

                        <Spinner animation="border" size="sm" /> Analisando banco legado...

                      </div>

                    )}

                    <div ref={chatEndRef} />

                  </Card.Body>

                  {nlAttachmentError && (

                    <div className="px-3 pb-2">

                      <Alert variant="warning" className="mb-0 py-2">{nlAttachmentError}</Alert>

                    </div>

                  )}

                  <Card.Footer>

                    <Form onSubmit={handleNaturalAsk}>

                      <Form.Group className="mb-2">

                        <Form.Control

                          as="textarea"

                          rows={3}

                          value={nlInput}

                          onChange={(e) => setNlInput(e.target.value)}

                          placeholder="Pergunte sobre incidentes, problemas, changes ou anexos (use INC/PRB/CHG)..."

                          maxLength={4000}

                          disabled={nlLoading || !status?.connected}

                        />

                      </Form.Group>

                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

                        <small className="text-muted">

                          Anexos aparecem abaixo da resposta quando você mencionar INC/PRB/CHG.

                        </small>

                        <Button

                          type="submit"

                          disabled={nlLoading || !nlInput.trim() || !status?.connected}

                        >

                          Perguntar

                        </Button>

                      </div>

                    </Form>

                  </Card.Footer>

                </Card>

              </Col>

              <Col lg={4}>

                <Card>

                  <Card.Header>Dicas</Card.Header>

                  <Card.Body className="small text-muted">

                    <p>Pergunte com o número do ticket para ver anexos na resposta.</p>

                    <p>As respostas em linguagem natural usam o mesmo assistente da Base de Conhecimento.</p>

                    <p>PDFs, imagens e e-mails (.eml) podem ser abertos no navegador.</p>

                    <p>No SQL, use <code>ar_incident</code> para a maioria dos INC (não só <code>incident</code>).</p>

                  </Card.Body>

                </Card>

              </Col>

            </Row>

          </Tab.Pane>



          <Tab.Pane eventKey="sql">

            <Card>

              <Card.Body>

                <Form onSubmit={handleSqlExecute}>

                  <Form.Group className="mb-3">

                    <Form.Label>Consulta SQL (somente SELECT)</Form.Label>

                    <Form.Control

                      as="textarea"

                      rows={8}

                      value={sqlInput}

                      onChange={(e) => setSqlInput(e.target.value)}

                      className="sn-legacy-sql-input"

                      spellCheck={false}

                      maxLength={20000}

                      disabled={sqlLoading || !status?.connected}

                    />

                  </Form.Group>

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setSqlInput(SQL_INCIDENT_WITH_ATTACHMENTS)}
                      disabled={sqlLoading}
                    >
                      Exemplo: incidente + anexos
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setSqlInput(SQL_ATTACHMENTS_ONLY)}
                      disabled={sqlLoading}
                    >
                      Exemplo: só anexos
                    </Button>
                  </div>

                  <Button type="submit" disabled={sqlLoading || !sqlInput.trim() || !status?.connected}>

                    {sqlLoading ? 'Executando...' : 'Executar SQL'}

                  </Button>

                </Form>

                <Alert variant="light" className="small mt-3 mb-0 sn-legacy-sql-hint">
                  <strong>Importante:</strong> o backup tem duas tabelas de incidente — <code>incident</code> (~47 mil)
                  e <code>ar_incident</code> (~437 mil). Cada INC está em <em>uma</em> delas; se a query retornar
                  0 linhas, use o botão <strong>Corrigir SQL</strong> quando aparecer.
                </Alert>

              </Card.Body>

            </Card>



            {sqlError && <Alert variant="danger" className="mt-3">{sqlError}</Alert>}



            {sqlResult && (
              <>
              <Card className="mt-3">

                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">

                  <span>Resultado</span>

                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {sqlResult.row_count > 0 && (
                      <ButtonGroup size="sm">
                        <Button
                          variant={
                            sqlViewMode !== 'table'
                            && (sqlViewMode === 'detail'
                              || (sqlViewMode === 'auto' && resolveSqlResultLayout(sqlResult) !== 'table'))
                              ? 'primary'
                              : 'outline-secondary'
                          }
                          onClick={() => setSqlViewMode('detail')}
                        >
                          {resolveSqlResultLayout(sqlResult) === 'journal' ? 'Leitura' : 'Ficha'}
                        </Button>
                        <Button
                          variant={
                            sqlViewMode === 'table'
                            || (sqlViewMode === 'auto' && resolveSqlResultLayout(sqlResult) === 'table')
                              ? 'primary'
                              : 'outline-secondary'
                          }
                          onClick={() => setSqlViewMode('table')}
                        >
                          Tabela
                        </Button>
                      </ButtonGroup>
                    )}

                    <small className="text-muted">

                      {sqlResult.row_count} linha(s) · {sqlResult.execution_ms} ms

                      {sqlResult.truncated ? ' · truncado' : ''}

                      {sqlResult.attachments?.length ? ` · ${sqlResult.attachments.length} anexo(s)` : ''}

                    </small>
                  </div>

                </Card.Header>

                <Card.Body>

                  <pre className="sn-legacy-sql-executed">{sqlResult.sql_executed}</pre>

                  <SqlResultDataView
                    result={sqlResult}
                    viewMode={sqlViewMode}
                    sqlHasInlineAttachments={sqlHasInlineAttachments}
                    authHeader={authHeader}
                    onAttachmentError={setSqlAttachmentError}
                    findSqlAttachmentItem={findSqlAttachmentItem}
                  />



                  {sqlResult.attachments?.length ? (

                    <LegacyAttachmentList

                      items={sqlResult.attachments}

                      authHeader={authHeader}

                      title="Anexos detectados nesta consulta"

                      onError={setSqlAttachmentError}

                    />

                  ) : null}



                  {sqlResult.record_hint && (
                    <Alert variant="info" className="mt-3 mb-0">
                      {sqlResult.record_hint.message}
                      {sqlResult.record_hint.suggested_sql ? (
                        <div className="mt-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              setSqlInput(sqlResult.record_hint!.suggested_sql!);
                              setSqlResult(null);
                            }}
                          >
                            Corrigir SQL ({sqlResult.record_hint.table})
                          </Button>
                        </div>
                      ) : null}
                    </Alert>
                  )}

                  {sqlAttachmentError && (

                    <Alert variant="warning" className="mt-3 mb-0">{sqlAttachmentError}</Alert>

                  )}

                </Card.Body>

              </Card>
              </>
            )}

          </Tab.Pane>

        </Tab.Content>

      </Tab.Container>

    </div>

  );

};



export default ServiceNowLegacyExplorer;


